import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, Calendar, Download, Plus, Trash2, Edit3, Save, X, ChevronLeft, FileText, Filter, GripVertical, Copy } from 'lucide-react';
import { T, CHART_COLORS, TRADER_TYPES, TRADER_COLORS, STATUS_COLORS, RISK_COLORS, fmtMonth, fmtNum, fmtMoney, safeNum, healthColor, healthLabel } from '../theme';
import { getGroupConfig } from '../riskConfig';
import { Card, Badge, RiskBadge, StatusBadge, GroupBadge, CoBadge, DataTable } from '../components/UI';

// ════════════════════════════════════════════════════════
// ═══ FIELD CATALOG — ALL 78+ FIELDS ══════════════════
// ════════════════════════════════════════════════════════
// fmt: text|money|number|decimal|pct|date|badge|risk|group|score|flag
// agg: same|sum|max|min|wavg|worst|union|earliest|latest|any|list|recalc
const FIELD_CATALOG = [
  { cat: "Identity", color: T.cyan, fields: [
    { id: "CU_Account", label: "CU Account", fmt: "text", agg: "same" },
    { id: "Login", label: "Login", fmt: "text", agg: "list" },
    { id: "Name", label: "Client Name", fmt: "text", agg: "same" },
    { id: "_company", label: "Company", fmt: "badge", agg: "same" },
    { id: "ClientType", label: "Client Type", fmt: "text", agg: "union" },
    { id: "Account_Type", label: "Account Type", fmt: "text", agg: "union" },
  ]},
  { cat: "Status & Risk", color: T.red, fields: [
    { id: "ClientStatus", label: "Status", fmt: "badge", agg: "worst" },
    { id: "BehavioralRisk", label: "Behavioral Risk", fmt: "risk", agg: "worst" },
    { id: "EquityRisk", label: "Equity Risk", fmt: "risk", agg: "worst" },
    { id: "healthScore", label: "Risk Score", fmt: "score", agg: "max" },
    { id: "_clientGroup", label: "Client Group", fmt: "group", agg: "worst" },
    { id: "ClientProfiling", label: "Client Profiling", fmt: "text", agg: "worst" },
  ]},
  { cat: "Financial (Period)", color: T.green, fields: [
    { id: "Deposit", label: "Deposit", fmt: "money", agg: "sum" },
    { id: "Withdraw", label: "Withdraw", fmt: "money", agg: "sum" },
    { id: "Volume", label: "Volume", fmt: "number", agg: "sum" },
    { id: "Profit", label: "Profit", fmt: "money", agg: "sum" },
    { id: "Equity", label: "Equity", fmt: "money", agg: "sum" },
    { id: "FloatingPL", label: "Floating P/L", fmt: "money", agg: "sum" },
    { id: "Credit", label: "Credit", fmt: "money", agg: "sum" },
  ]},
  { cat: "Financial (Lifetime)", color: "#34d399", fields: [
    { id: "T.Deposit", label: "Total Deposit", fmt: "money", agg: "sum" },
    { id: "T.Withdraw", label: "Total Withdraw", fmt: "money", agg: "sum" },
    { id: "T.Volume", label: "Total Volume", fmt: "number", agg: "sum" },
    { id: "T.Profit", label: "Total Profit", fmt: "money", agg: "sum" },
    { id: "T.Credit", label: "Total Credit", fmt: "money", agg: "sum" },
  ]},
  { cat: "Trading", color: T.amber, fields: [
    { id: "TotalTrades", label: "Total Trades", fmt: "number", agg: "sum" },
    { id: "TotalDeals", label: "Total Deals", fmt: "number", agg: "sum" },
    { id: "ProfitTrades", label: "Win Trades", fmt: "number", agg: "sum" },
    { id: "LossTrades", label: "Loss Trades", fmt: "number", agg: "sum" },
    { id: "ProfitPct", label: "Win %", fmt: "pct", agg: "recalc" },
    { id: "LossPct", label: "Loss %", fmt: "pct", agg: "recalc" },
    { id: "MostUsedTradeSize", label: "Trade Size", fmt: "decimal", agg: "wavg" },
    { id: "MostInstrument", label: "Most Instrument", fmt: "text", agg: "union" },
    { id: "MostTradedInstruments", label: "Instrument Category", fmt: "text", agg: "union" },
    { id: "MostActiveDay", label: "Most Active Day", fmt: "text", agg: "union" },
    { id: "DailyVolumeLots", label: "Daily Volume (lots)", fmt: "decimal", agg: "sum" },
    { id: "TradingFreqPerDay", label: "Trades / Day", fmt: "decimal", agg: "wavg" },
    { id: "AvgTradeDuration", label: "Avg Trade Duration (s)", fmt: "number", agg: "wavg" },
    { id: "AvgTimeCloseLosingTrades", label: "Avg Close Loss (s)", fmt: "number", agg: "wavg" },
    { id: "AvgTimeCloseProfitableTrades", label: "Avg Close Profit (s)", fmt: "number", agg: "wavg" },
    { id: "TotalNetProfit", label: "Net Profit", fmt: "money", agg: "sum" },
    { id: "GrossProfit", label: "Gross Profit", fmt: "money", agg: "sum" },
    { id: "GrossLoss", label: "Gross Loss", fmt: "money", agg: "sum" },
    { id: "BestTrade", label: "Best Trade", fmt: "money", agg: "max" },
    { id: "WorstTrade", label: "Worst Trade", fmt: "money", agg: "min" },
    { id: "ProfitFactor", label: "Profit Factor", fmt: "decimal", agg: "wavg" },
    { id: "LongPct", label: "Long %", fmt: "pct", agg: "wavg" },
    { id: "AvgProfitTrade", label: "Avg Profit Trade", fmt: "money", agg: "wavg" },
    { id: "AvgLossTrade", label: "Avg Loss Trade", fmt: "money", agg: "wavg" },
    { id: "MaxConsecutiveWins", label: "Max Consec. Wins", fmt: "number", agg: "max" },
    { id: "ExpectedPayoff", label: "Expected Payoff", fmt: "money", agg: "wavg" },
    { id: "StillOpenPositions", label: "Open Positions", fmt: "number", agg: "sum" },
    { id: "StillOpenVolumeLots", label: "Open Volume (lots)", fmt: "decimal", agg: "sum" },
    { id: "OrdersHeldGT10Days", label: "Held > 10 Days", fmt: "number", agg: "sum" },
    { id: "VolumeHeldGT10DaysLots", label: "Vol Held >10 Days (lots)", fmt: "decimal", agg: "sum" },
    { id: "SwapInOut", label: "Swap In/Out", fmt: "money", agg: "sum" },
    { id: "ActualSwap", label: "Actual Swap", fmt: "money", agg: "sum" },
  ]},
  { cat: "Speed & Abuse", color: T.orange, fields: [
    { id: "PctUnder30Sec", label: "Speed <30s %", fmt: "pct", agg: "wavg" },
    { id: "PctUnder2Min", label: "Speed <2min %", fmt: "pct", agg: "wavg" },
    { id: "PctUnder5Min", label: "Speed <5min %", fmt: "pct", agg: "wavg" },
    { id: "SpeedConsistency", label: "Speed Consistency", fmt: "decimal", agg: "wavg" },
    { id: "FastTradeWinRate", label: "Fast Win Rate %", fmt: "pct", agg: "wavg" },
    { id: "SlowTradeWinRate", label: "Slow Win Rate %", fmt: "pct", agg: "wavg" },
    { id: "HedgeRatio", label: "Hedge Ratio", fmt: "decimal", agg: "wavg" },
    { id: "HedgeDealsCount", label: "Hedge Deals", fmt: "number", agg: "sum" },
    { id: "HedgeVolumeLots", label: "Hedge Volume (lots)", fmt: "decimal", agg: "sum" },
    { id: "AvgHedgeDurationSec", label: "Avg Hedge Duration (s)", fmt: "number", agg: "wavg" },
    { id: "FridayLateOpens", label: "Friday Late Opens", fmt: "number", agg: "sum" },
    { id: "FridayLateOpenLots", label: "Friday Late Open (lots)", fmt: "decimal", agg: "sum" },
    { id: "FridayHedgeBreaks", label: "Friday Hedge Breaks", fmt: "number", agg: "sum" },
    { id: "FridayHedgeBreakLots", label: "Friday HB (lots)", fmt: "decimal", agg: "sum" },
    { id: "TradesNearNews10Min", label: "News Trades (10min)", fmt: "number", agg: "sum" },
    { id: "CommissionRebateExploitation", label: "Commission Exploit", fmt: "money", agg: "sum" },
    { id: "RemainingSwap", label: "Remaining Swap", fmt: "money", agg: "sum" },
    { id: "IP_ODT", label: "IP ODT", fmt: "number", agg: "sum" },
    { id: "Login_Same_IP", label: "Login Same IP", fmt: "text", agg: "union" },
    { id: "CU_Same_IP", label: "CU Same IP", fmt: "text", agg: "union" },
  ]},
  { cat: "Account Info", color: T.blue, fields: [
    { id: "A.M", label: "Account Manager", fmt: "text", agg: "same" },
    { id: "Country", label: "Country", fmt: "text", agg: "same" },
    { id: "Registration", label: "Registration", fmt: "date", agg: "earliest" },
    { id: "LastAccess", label: "Last Access", fmt: "date", agg: "latest" },
    { id: "CRM_Account_Open_Date", label: "CRM Open Date", fmt: "date", agg: "earliest" },
    { id: "First_Deposit", label: "First Deposit", fmt: "money", agg: "min" },
    { id: "First_Deposit_Time", label: "First Deposit Time", fmt: "date", agg: "earliest" },
    { id: "Deposit_Count", label: "Deposit Count", fmt: "number", agg: "sum" },
    { id: "LifetimeDays", label: "Lifetime (days)", fmt: "number", agg: "max" },
    { id: "Leverage", label: "Leverage", fmt: "text", agg: "union" },
    { id: "IP", label: "IP Address", fmt: "text", agg: "union" },
    { id: "RelatedAccounts", label: "Related Accounts", fmt: "text", agg: "union" },
    { id: "RelatedAccountsTotalProfit", label: "Related Profit", fmt: "money", agg: "sum" },
    { id: "EquityPrevDay", label: "Equity Prev Day", fmt: "money", agg: "sum" },
    { id: "EquityPrevMonth", label: "Equity Prev Month", fmt: "money", agg: "sum" },
    { id: "Phone_Number", label: "Phone Number", fmt: "text", agg: "same" },
  ]},
  { cat: "Trader Flags", color: T.purple, fields: [
    { id: "HeavyTrader", label: "Heavy Trader", fmt: "flag", agg: "any" },
    { id: "HighFrequencyTrader", label: "High Freq. Trader", fmt: "flag", agg: "any" },
    { id: "Scalper", label: "Scalper", fmt: "flag", agg: "any" },
    { id: "Hedger", label: "Hedger", fmt: "flag", agg: "any" },
    { id: "HedgingAbuser", label: "Hedging Abuser", fmt: "flag", agg: "any" },
    { id: "Swaper", label: "Swaper", fmt: "flag", agg: "any" },
    { id: "LongTrader", label: "Long Trader", fmt: "flag", agg: "any" },
    { id: "NewsTrader", label: "News Trader", fmt: "flag", agg: "any" },
    { id: "GainerTrader", label: "Gainer Trader", fmt: "flag", agg: "any" },
    { id: "Commissioner", label: "Commissioner", fmt: "flag", agg: "any" },
  ]},
  { cat: "Calculated", color: "#ec4899", fields: [
    { id: "_calc_net", label: "Net (Dep - Wdr)", fmt: "money", agg: "sum", calc: r => safeNum(r.Deposit) + safeNum(r.Withdraw) },
    { id: "_calc_tnet", label: "Lifetime Net", fmt: "money", agg: "sum", calc: r => safeNum(r["T.Deposit"]) + safeNum(r["T.Withdraw"]) },
    { id: "_calc_winpct", label: "Win %", fmt: "pct", agg: "recalc", calc: r => { const t = safeNum(r.TotalTrades); return t > 0 ? safeNum(r.ProfitTrades) / t * 100 : 0; } },
    { id: "_calc_roi", label: "ROI %", fmt: "pct", agg: "recalc", calc: r => { const d = safeNum(r.Deposit); return d > 0 ? safeNum(r.Profit) / d * 100 : 0; } },
    { id: "_calc_eqratio", label: "Equity / Deposit", fmt: "decimal", agg: "recalc", calc: r => { const d = safeNum(r["T.Deposit"]); return d > 0 ? safeNum(r.Equity) / d : 0; } },
  ]},
];

const ALL_FIELDS = FIELD_CATALOG.flatMap(c => c.fields.map(f => ({ ...f, cat: c.cat, catColor: c.color })));
const FIELD_MAP = {}; ALL_FIELDS.forEach(f => { FIELD_MAP[f.id] = f; });

const FMT_TO_FILTER = { money: "range", number: "range", decimal: "range", pct: "range", score: "range", text: "dropdown", badge: "dropdown", risk: "dropdown", group: "dropdown", date: "dateRange", flag: "boolean" };
const FILTER_TYPES = {
  dropdown: { label: "Dropdown", icon: "▾", color: T.cyan },
  range: { label: "Range", icon: "↕", color: T.amber },
  above: { label: "Above ≥", icon: "≥", color: T.green },
  below: { label: "Below ≤", icon: "≤", color: T.red },
  dateRange: { label: "Date Range", icon: "📅", color: T.purple },
  boolean: { label: "Yes / No", icon: "✓", color: T.orange },
  text: { label: "Contains", icon: "Aa", color: T.blue },
};

// ─── MULTI-SELECT DROPDOWN FOR FILTERS ───
function FilterSelect({ label, values, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = search ? values.filter(v => String(v).toLowerCase().includes(search.toLowerCase())) : values;
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);

  const popupItems = [];
  for (let i = 0; i < filtered.length; i++) {
    const v = String(filtered[i]);
    const isChecked = selected.includes(v);
    popupItems.push(
      React.createElement("label", {
        key: "flt_" + i,
        style: { display: "block", padding: "6px 12px", cursor: "pointer", fontSize: 11, color: isChecked ? "#e2e8f0" : "#8494a7", backgroundColor: isChecked ? "rgba(6,182,212,0.08)" : "transparent", userSelect: "none" },
      },
        React.createElement("input", { type: "checkbox", checked: isChecked, onChange: () => toggle(v), style: { marginRight: 8, accentColor: "#06b6d4" } }),
        v
      )
    );
  }

  return (
    <div ref={ref} style={{ display: "inline-flex", alignItems: "center", gap: 4, position: "relative" }}>
      <span style={{ fontSize: 9, color: "#4a5568", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}:</span>
      <button onClick={() => { setOpen(!open); setSearch(""); }}
        style={{ padding: "4px 10px", borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: "pointer",
          border: selected.length > 0 ? "1px solid #06b6d4" : "1px solid #1e2330", background: selected.length > 0 ? "rgba(6,182,212,0.15)" : "#1a1e26", color: selected.length > 0 ? "#06b6d4" : "#8494a7" }}>
        {selected.length > 0 ? selected.length + " selected" : "All"} ▼
      </button>
      {selected.length > 0 && <button onClick={() => onChange([])} style={{ padding: "2px 5px", borderRadius: 3, border: "none", background: "rgba(239,68,68,0.15)", color: "#ef4444", cursor: "pointer", fontSize: 9, fontWeight: 700 }}>✕</button>}
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, width: 260, background: "#1a1e26", border: "2px solid #2a3040", borderRadius: 8, zIndex: 9999, boxShadow: "0 12px 40px rgba(0,0,0,0.7)" }}>
          {values.length > 8 && (
            <div style={{ padding: 8, borderBottom: "1px solid #2a3040" }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={"Search " + values.length + " items..."} autoFocus
                style={{ width: "100%", padding: "6px 10px", borderRadius: 5, border: "1px solid #2a3040", background: "#0b0d11", color: "#e2e8f0", fontSize: 11, outline: "none", boxSizing: "border-box" }} />
            </div>
          )}
          <div style={{ padding: "4px 10px", borderBottom: "1px solid rgba(42,48,64,0.4)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span onClick={() => onChange([...values])} style={{ color: "#06b6d4", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>Select All</span>
            <span style={{ fontSize: 9, color: "#4a5568" }}>{filtered.length} of {values.length}</span>
            <span onClick={() => onChange([])} style={{ color: "#ef4444", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>Clear</span>
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {popupItems.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "#4a5568", fontSize: 11 }}>No results</div>}
            {popupItems}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ═══ LOCALSTORAGE HELPERS ════════════════════════════
// ════════════════════════════════════════════════════════
const LS_KEY = "cp_report_templates";
export const getReportConfigs = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; } };
export const saveReportConfigs = (configs) => localStorage.setItem(LS_KEY, JSON.stringify(configs));

// ════════════════════════════════════════════════════════
// ═══ DATA HELPERS ════════════════════════════════════
// ════════════════════════════════════════════════════════
const getFieldValue = (row, fieldId) => {
  const fDef = FIELD_MAP[fieldId];
  if (!fDef) return row[fieldId];
  if (fDef.calc) return fDef.calc(row);
  if (fDef.fmt === "flag") return row[fieldId] === fieldId ? "Yes" : "No";
  return row[fieldId];
};

const getUniqueValues = (data, fieldId) => {
  if (!data || !data.length) return [];
  const fDef = FIELD_MAP[fieldId];
  if (fDef?.fmt === "flag") return ["Yes", "No"];
  const vals = [];
  data.forEach(r => {
    let v = fDef?.calc ? fDef.calc(r) : r[fieldId];
    if (v == null || v === "" || v === "NULL" || v === undefined || v === 0 && fDef?.fmt === "text") return;
    vals.push(String(v));
  });
  return [...new Set(vals)].sort();
};

// CU-level aggregation
const aggregateCU = (rows, columns) => {
  const cuMap = {};
  rows.forEach(r => {
    const cu = r.CU_Account || r.Login;
    const co = r._company || "";
    const k = co + "|" + cu;
    if (!cuMap[k]) cuMap[k] = [];
    cuMap[k].push(r);
  });
  return Object.values(cuMap).map(group => {
    const result = { ...group[0] }; // base from first row
    if (group.length <= 1) return result;
    columns.forEach(col => {
      const fDef = FIELD_MAP[col.fieldId];
      if (!fDef || fDef.agg === "same") return;
      const vals = group.map(r => r[col.fieldId]).filter(v => v != null && v !== "NULL");
      const nums = group.map(r => safeNum(r[col.fieldId]));
      switch (fDef.agg) {
        case "sum": result[col.fieldId] = nums.reduce((s, v) => s + v, 0); break;
        case "max": result[col.fieldId] = Math.max(...nums); break;
        case "min": result[col.fieldId] = Math.min(...nums.filter(n => n !== 0)); break;
        case "worst": {
          if (fDef.fmt === "risk") { result[col.fieldId] = vals.includes("High") ? "High" : vals.includes("Moderate") ? "Moderate" : (vals[0] || "Normal"); }
          else if (fDef.fmt === "badge" && col.fieldId === "ClientStatus") { result[col.fieldId] = vals.includes("Active") ? "Active" : vals.includes("Dormant") ? "Dormant" : "Not Active"; }
          else if (col.fieldId === "_clientGroup") {
            const go = getGroupConfig().groupOrder || ["STD_B","STD_AB","PRO_A","SPD_A","HDG_A","B2B_A","CRT_A"];
            let worst = vals[0]; vals.forEach(v => { if (go.indexOf(v) > go.indexOf(worst)) worst = v; }); result[col.fieldId] = worst;
          } else { result[col.fieldId] = vals[0]; }
          break;
        }
        case "union": result[col.fieldId] = [...new Set(vals)].join(", "); break;
        case "list": result[col.fieldId] = group.map(r => r[col.fieldId]).filter(Boolean).join(", "); break;
        case "earliest": { let e = null; vals.forEach(v => { const d = new Date(v); if (!isNaN(d) && (!e || d < e)) e = d; }); result[col.fieldId] = e || vals[0]; break; }
        case "latest": { let l = null; vals.forEach(v => { const d = new Date(v); if (!isNaN(d) && (!l || d > l)) l = d; }); result[col.fieldId] = l || vals[0]; break; }
        case "wavg": { let tw = 0, tv = 0; group.forEach(r => { const w = safeNum(r.TotalTrades) || safeNum(r.Volume); if (w > 0) { tw += w; tv += safeNum(r[col.fieldId]) * w; } }); result[col.fieldId] = tw > 0 ? tv / tw : 0; break; }
        case "any": result[col.fieldId] = group.some(r => r[col.fieldId] === col.fieldId || r[col.fieldId] === "Yes") ? col.fieldId : ""; break;
        case "recalc": if (fDef.calc) result[col.fieldId] = fDef.calc(result); break;
        default: break;
      }
    });
    result._loginCount = group.length;
    return result;
  });
};

// ════════════════════════════════════════════════════════
// ═══ CELL RENDERER ══════════════════════════════════
// ════════════════════════════════════════════════════════
const renderCellValue = (val, fmt, fieldId) => {
  if (val == null || val === "NULL" || val === "") return <span style={{ color: T.textMuted }}>—</span>;
  if (fmt === "money") return <span className="mono" style={{ color: safeNum(val) >= 0 ? T.green : T.red }}>{fmtMoney(val)}</span>;
  if (fmt === "number") return <span className="mono">{fmtNum(safeNum(val), 0)}</span>;
  if (fmt === "decimal") return <span className="mono">{fmtNum(safeNum(val), 2)}</span>;
  if (fmt === "pct") { const n = safeNum(val); return <span className="mono" style={{ color: n > 50 ? T.red : n > 20 ? T.orange : T.green }}>{fmtNum(n, 1)}%</span>; }
  if (fmt === "score") { const s = safeNum(val); return <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 40, height: 5, background: T.bgHover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: Math.min(s, 100) + "%", background: healthColor(s), borderRadius: 3 }} /></div><span className="mono" style={{ fontSize: 10, fontWeight: 700, color: healthColor(s) }}>{s}</span></div>; }
  if (fmt === "risk") return <RiskBadge level={val} />;
  if (fmt === "group") return <GroupBadge group={val} client={{}} />;
  if (fmt === "badge") {
    if (fieldId === "ClientStatus") return <StatusBadge status={val} />;
    if (fieldId === "_company") return <CoBadge co={val} size={18} />;
    return <Badge text={String(val)} color={T.cyan} bg={T.cyanGlow} />;
  }
  if (fmt === "flag") {
    const yes = val === fieldId || val === "Yes";
    return <span style={{ color: yes ? T.red : T.textMuted, fontWeight: 600, fontSize: 11 }}>{yes ? "✓ Yes" : "—"}</span>;
  }
  if (fmt === "date") {
    if (val instanceof Date) return <span style={{ fontSize: 11, color: T.textDim }}>{val.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>;
    const d = new Date(val); if (!isNaN(d) && d.getFullYear() > 1990) return <span style={{ fontSize: 11, color: T.textDim }}>{d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>;
    return <span style={{ fontSize: 11, color: T.textDim }}>{String(val).split("T")[0]}</span>;
  }
  return <span style={{ color: T.text, fontSize: 12 }}>{String(val).substring(0, 60)}</span>;
};

// Export cell as plain text for Excel
const cellToText = (val, fmt, fieldId) => {
  if (val == null || val === "NULL") return "";
  if (fmt === "flag") return (val === fieldId || val === "Yes") ? "Yes" : "No";
  if (fmt === "date" && val instanceof Date) return val.toISOString().split("T")[0];
  return val;
};

// ════════════════════════════════════════════════════════
// ═══ REPORT DESIGNER (for Admin panel) ══════════════
// ════════════════════════════════════════════════════════
export function ReportDesigner({ onSave, onCancel, editingReport }) {
  const [report, setReport] = useState(editingReport || { id: "r_" + Date.now(), name: "", description: "", level: "login", columns: [], filters: [], createdAt: new Date().toISOString().split("T")[0] });
  const [catFilter, setCatFilter] = useState("all");
  const [fieldSearch, setFieldSearch] = useState("");
  const [showFilterPicker, setShowFilterPicker] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const filteredFields = ALL_FIELDS.filter(f => (catFilter === "all" || f.cat === catFilter) && (!fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.id.toLowerCase().includes(fieldSearch.toLowerCase())));

  const addCol = (f) => {
    if (report.columns.find(c => c.fieldId === f.id)) return;
    setReport({ ...report, columns: [...report.columns, { fieldId: f.id, label: f.label, fmt: f.fmt }] });
  };
  const removeCol = (i) => { const c = [...report.columns]; c.splice(i, 1); setReport({ ...report, columns: c }); };
  const moveCol = (from, to) => { const c = [...report.columns]; const item = c.splice(from, 1)[0]; c.splice(to, 0, item); setReport({ ...report, columns: c }); };
  const addFilter = (f) => {
    if (report.filters.find(rf => rf.fieldId === f.id)) return;
    const type = FMT_TO_FILTER[f.fmt] || "dropdown";
    setReport({ ...report, filters: [...report.filters, { fieldId: f.id, type, label: f.label, defaultValue: "" }] });
    setShowFilterPicker(false);
  };
  const removeFilter = (i) => { const f = [...report.filters]; f.splice(i, 1); setReport({ ...report, filters: f }); };
  const updateFilter = (i, key, val) => { const f = [...report.filters]; f[i] = { ...f[i], [key]: val }; setReport({ ...report, filters: f }); };

  const handleSave = () => { if (!report.name.trim()) return alert("Report name is required"); onSave(report); };

  return (
    <div style={{ display: "flex", gap: 14, height: "calc(100vh - 200px)", minHeight: 500 }}>
      {/* LEFT: Field Catalog */}
      <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Card accent={T.cyan} style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.cyan, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Field Catalog</div>
          <input value={fieldSearch} onChange={e => setFieldSearch(e.target.value)} placeholder="Search fields..."
            style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 11, outline: "none", marginBottom: 6, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 8 }}>
            <button onClick={() => setCatFilter("all")} style={{ padding: "3px 8px", borderRadius: 4, border: catFilter === "all" ? "1px solid " + T.cyan : "1px solid " + T.border, background: catFilter === "all" ? T.cyanGlow : "transparent", color: catFilter === "all" ? T.cyan : T.textMuted, cursor: "pointer", fontSize: 9, fontWeight: 600 }}>All</button>
            {FIELD_CATALOG.map(c => (
              <button key={c.cat} onClick={() => setCatFilter(c.cat)} style={{ padding: "3px 8px", borderRadius: 4, border: catFilter === c.cat ? "1px solid " + c.color : "1px solid " + T.border, background: catFilter === c.cat ? c.color + "15" : "transparent", color: catFilter === c.cat ? c.color : T.textMuted, cursor: "pointer", fontSize: 9, fontWeight: 600 }}>{c.cat.split(" ")[0]}</button>
            ))}
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {filteredFields.map(f => {
              const added = report.columns.find(c => c.fieldId === f.id);
              return (
                <div key={f.id} onClick={() => !added && addCol(f)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", marginBottom: 2, borderRadius: 5, cursor: added ? "default" : "pointer", background: added ? T.green + "06" : "transparent", opacity: added ? 0.4 : 1 }}>
                  <span style={{ width: 3, height: 16, borderRadius: 2, background: f.catColor, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.text, flex: 1 }}>{f.label}</span>
                  {added ? <span style={{ fontSize: 10, color: T.green }}>✓</span> : <span style={{ fontSize: 13, color: T.cyan }}>+</span>}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* RIGHT: Config + Preview */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, overflow: "hidden" }}>
        {/* Report settings */}
        <Card style={{ flexShrink: 0, padding: 14 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Report Name *</label>
              <input value={report.name} onChange={e => setReport({ ...report, name: e.target.value })} placeholder="e.g., Boss Summary"
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 13, fontWeight: 600, outline: "none", marginTop: 3, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 2, minWidth: 250 }}>
              <label style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Description</label>
              <input value={report.description} onChange={e => setReport({ ...report, description: e.target.value })} placeholder="Brief description..."
                style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 11, outline: "none", marginTop: 3, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, display: "block" }}>Level</label>
              <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                {[["login", "Per Login", T.cyan], ["cu", "Per CU", T.amber]].map(([v, l, c]) => (
                  <button key={v} onClick={() => setReport({ ...report, level: v })} style={{ padding: "7px 12px", borderRadius: 6, border: report.level === v ? "1px solid " + c : "1px solid " + T.border, background: report.level === v ? c + "15" : "transparent", color: report.level === v ? c : T.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Columns */}
        <Card accent={T.amber} style={{ flexShrink: 0, padding: 14, maxHeight: 120, overflow: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.amber }}>COLUMNS ({report.columns.length}) <span style={{ fontWeight: 400, color: T.textDim }}>— click fields on left to add, drag to reorder</span></div>
          </div>
          {report.columns.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", color: T.textMuted, border: "2px dashed " + T.border, borderRadius: 8, fontSize: 12 }}>Click fields from catalog →</div>
          ) : (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {report.columns.map((col, i) => {
                const fDef = FIELD_MAP[col.fieldId];
                return (
                  <div key={col.fieldId} draggable onDragStart={() => setDragIdx(i)} onDragOver={e => e.preventDefault()} onDrop={() => { if (dragIdx !== null) moveCol(dragIdx, i); setDragIdx(null); }}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 8px", borderRadius: 6, background: (fDef?.catColor || T.textMuted) + "10", border: "1px solid " + (fDef?.catColor || T.border) + "30", cursor: "grab", fontSize: 11 }}>
                    <span style={{ color: T.textMuted, fontSize: 9 }}>⠿</span>
                    <span style={{ fontWeight: 600, color: T.text }}>{col.label}</span>
                    <span onClick={(e) => { e.stopPropagation(); removeCol(i); }} style={{ cursor: "pointer", color: T.red, fontWeight: 700, fontSize: 12 }}>×</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Filters */}
        <Card accent={T.purple} style={{ flexShrink: 0, padding: 14, maxHeight: 200, overflow: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.purple }}>FILTERS ({report.filters.length}) <span style={{ fontWeight: 400, color: T.textDim }}>— users see these as filter controls</span></div>
            <button onClick={() => setShowFilterPicker(!showFilterPicker)} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid " + T.purple, background: T.purple + "15", color: T.purple, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{showFilterPicker ? "✕ Cancel" : "+ Add Filter"}</button>
          </div>
          {showFilterPicker && (
            <div style={{ background: T.bg, borderRadius: 8, padding: 10, marginBottom: 8, border: "1px solid " + T.purple + "25", maxHeight: 120, overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3 }}>
                {ALL_FIELDS.filter(f => !report.filters.find(rf => rf.fieldId === f.id)).map(f => (
                  <div key={f.id} onClick={() => addFilter(f)} style={{ padding: "5px 8px", borderRadius: 4, cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid " + T.border + "40" }}>
                    <span style={{ width: 3, height: 12, borderRadius: 2, background: f.catColor }} />
                    <span style={{ color: T.text }}>{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {report.filters.map((f, i) => {
            const fDef = FIELD_MAP[f.fieldId];
            const isNum = ["money", "number", "decimal", "pct", "score"].includes(fDef?.fmt);
            const isDate = fDef?.fmt === "date";
            const isFlag = fDef?.fmt === "flag";
            const availTypes = Object.entries(FILTER_TYPES).filter(([type]) => {
              if (type === "dateRange" && !isDate) return false;
              if (type === "boolean" && !isFlag) return false;
              if ((type === "range" || type === "above" || type === "below") && !isNum) return false;
              if (type === "dropdown" && isFlag) return false;
              if (type === "text" && isNum) return false;
              return true;
            });
            return (
              <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, background: T.bg, border: "1px solid " + T.border, marginBottom: 4 }}>
                <span style={{ width: 3, height: 14, borderRadius: 2, background: fDef?.catColor || T.textMuted }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, minWidth: 120 }}>{f.label}</span>
                <div style={{ display: "flex", gap: 3 }}>
                  {availTypes.map(([type, def]) => (
                    <button key={type} onClick={() => updateFilter(i, "type", type)} style={{ padding: "3px 8px", borderRadius: 4, border: f.type === type ? "1px solid " + def.color : "1px solid " + T.border + "50", background: f.type === type ? def.color + "15" : "transparent", color: f.type === type ? def.color : T.textMuted, cursor: "pointer", fontSize: 9, fontWeight: 600 }}>{def.icon} {def.label}</button>
                  ))}
                </div>
                {(f.type === "above" || f.type === "below") && (
                  <input value={f.defaultValue || ""} onChange={e => updateFilter(i, "defaultValue", e.target.value)} placeholder="default" style={{ width: 60, padding: "3px 6px", borderRadius: 4, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 10, outline: "none", fontFamily: "monospace" }} />
                )}
                <span onClick={() => removeFilter(i)} style={{ cursor: "pointer", color: T.red, fontWeight: 700, fontSize: 12, marginLeft: "auto" }}>×</span>
              </div>
            );
          })}
        </Card>

        {/* Save/Cancel */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0 }}>
          <button onClick={onCancel} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid " + T.border, background: "transparent", color: T.textDim, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid " + T.green, background: T.green, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>💾 Save Report</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ═══ REPORT VIEWER ══════════════════════════════════
// ════════════════════════════════════════════════════════
function ReportViewer({ report, C, onBack, onClientClick }) {
  const [search, setSearch] = useState("");
  const [userFilters, setUserFilters] = useState(() => {
    const init = {};
    (report.filters || []).forEach(f => {
      if ((f.type === "above" || f.type === "below") && f.defaultValue) init[f.fieldId] = f.defaultValue;
    });
    return init;
  });

  // Build data from C
  const rawData = useMemo(() => {
    // Primary source: healthScores (has all fields + computed healthScore, _clientGroup)
    const primary = C.healthScores || C.clients || [];
    // Secondary: latestAllClients for T.* lifetime totals and Equity snapshot
    const latMap = {};
    (C.latestAllClients || []).forEach(r => { latMap[r._company + "|" + r.Login] = r; });
    return primary.map(r => {
      const lat = latMap[r._company + "|" + r.Login];
      if (!lat) return r;
      // Only merge T.* fields and current snapshots from latestAllClients, keep everything else from healthScores
      return { ...r,
        "T.Deposit": lat["T.Deposit"], "T.Withdraw": lat["T.Withdraw"], "T.Volume": lat["T.Volume"], "T.Profit": lat["T.Profit"], "T.Credit": lat["T.Credit"],
      };
    });
  }, [C]);

  const processedData = useMemo(() => {
    let data = [...rawData];
    // CU-level aggregation
    if (report.level === "cu") data = aggregateCU(data, report.columns);
    // Compute calculated fields
    data = data.map(r => {
      const nr = { ...r };
      report.columns.forEach(col => { const fDef = FIELD_MAP[col.fieldId]; if (fDef?.calc) nr[col.fieldId] = fDef.calc(nr); });
      return nr;
    });
    return data;
  }, [rawData, report.level, report.columns]);

  // Apply filters
  const filteredData = useMemo(() => {
    let data = [...processedData];
    if (search) {
      const s = search.toLowerCase();
      data = data.filter(row => report.columns.some(col => {
        const v = getFieldValue(row, col.fieldId);
        return v != null && String(v).toLowerCase().includes(s);
      }));
    }
    Object.entries(userFilters).forEach(([fieldId, val]) => {
      const fCfg = (report.filters || []).find(f => f.fieldId === fieldId);
      if (!fCfg || val === undefined || val === "") return;
      if (fCfg.type === "dropdown" && Array.isArray(val) && val.length > 0) data = data.filter(r => val.includes(String(getFieldValue(r, fieldId))));
      if (fCfg.type === "above" && val !== "") data = data.filter(r => safeNum(getFieldValue(r, fieldId)) >= Number(val));
      if (fCfg.type === "below" && val !== "") data = data.filter(r => safeNum(getFieldValue(r, fieldId)) <= Number(val));
      if (fCfg.type === "range" && typeof val === "object") {
        if (val.min !== "" && val.min !== undefined) data = data.filter(r => safeNum(getFieldValue(r, fieldId)) >= Number(val.min));
        if (val.max !== "" && val.max !== undefined) data = data.filter(r => safeNum(getFieldValue(r, fieldId)) <= Number(val.max));
      }
      if (fCfg.type === "boolean") data = data.filter(r => { const v = getFieldValue(r, fieldId); return val === "Yes" ? (v === fieldId || v === "Yes") : (v !== fieldId && v !== "Yes"); });
      if (fCfg.type === "text" && val) data = data.filter(r => String(getFieldValue(r, fieldId) || "").toLowerCase().includes(val.toLowerCase()));
      if (fCfg.type === "dateRange" && typeof val === "object") {
        if (val.from) data = data.filter(r => { const d = new Date(r[fieldId]); return !isNaN(d) && d >= new Date(val.from); });
        if (val.to) data = data.filter(r => { const d = new Date(r[fieldId]); return !isNaN(d) && d <= new Date(val.to); });
      }
    });
    return data;
  }, [processedData, search, userFilters, report.filters, report.columns]);

  const hasActiveFilters = search || Object.values(userFilters).some(v => v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && !Array.isArray(v) && (v.min === "" || v.min === undefined) && (v.max === "" || v.max === undefined) && !v.from && !v.to));

  // Export
  const handleExport = useCallback(() => {
    const header = report.columns.map(c => c.label);
    const rows = filteredData.map(r => report.columns.map(col => cellToText(getFieldValue(r, col.fieldId), col.fmt, col.fieldId)));
    const csv = [header, ...rows].map(r => r.map(v => '"' + String(v ?? "").replace(/"/g, '""') + '"').join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = (report.name || "Report").replace(/[^a-zA-Z0-9]/g, "_") + ".csv"; a.click();
    URL.revokeObjectURL(url);
  }, [filteredData, report]);

  // Build DataTable columns
  const tableColumns = report.columns.map(col => ({
    key: col.fieldId, label: col.label,
    render: (v, row) => renderCellValue(getFieldValue(row, col.fieldId), col.fmt, col.fieldId),
    sortFn: col.fmt === "money" || col.fmt === "number" || col.fmt === "decimal" || col.fmt === "pct" || col.fmt === "score"
      ? (a, b) => safeNum(getFieldValue(a, col.fieldId)) - safeNum(getFieldValue(b, col.fieldId))
      : undefined,
  }));

  // Dropdown filter values — use rawData (not processedData) to ensure all field values available
  // Also check C.allData as fallback since it has all months of raw Excel data
  const dropdownVals = useMemo(() => {
    const m = {};
    (report.filters || []).filter(f => f.type === "dropdown").forEach(f => {
      // Try rawData first
      let vals = getUniqueValues(rawData, f.fieldId);
      // If empty, try allData (raw unprocessed Excel data)
      if (vals.length === 0 && C.allData) vals = getUniqueValues(C.allData, f.fieldId);
      // If still empty, try latestAllClients
      if (vals.length === 0 && C.latestAllClients) vals = getUniqueValues(C.latestAllClients, f.fieldId);
      m[f.fieldId] = vals;
    });
    return m;
  }, [rawData, report.filters]);

  const dropdowns = (report.filters || []).filter(f => f.type === "dropdown");
  const numerics = (report.filters || []).filter(f => f.type === "range" || f.type === "above" || f.type === "below");
  const dateRanges = (report.filters || []).filter(f => f.type === "dateRange");
  const booleans = (report.filters || []).filter(f => f.type === "boolean");
  const textFilters = (report.filters || []).filter(f => f.type === "text");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid " + T.border, background: "transparent", color: T.textDim, cursor: "pointer", fontSize: 12 }}><ChevronLeft size={15} /> Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{report.name}</div>
          <div style={{ fontSize: 11, color: T.textDim }}>{report.description}</div>
        </div>
        <span style={{ padding: "4px 12px", borderRadius: 6, fontSize: 10, fontWeight: 700, color: report.level === "cu" ? T.amber : T.cyan, background: (report.level === "cu" ? T.amber : T.cyan) + "15" }}>{report.level === "cu" ? "CU Level" : "Login Level"}</span>
        <button onClick={handleExport} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid " + T.green, background: T.green + "15", color: T.green, cursor: "pointer", fontSize: 12, fontWeight: 600 }}><Download size={14} /> Export CSV</button>
      </div>

      {/* Filter bar */}
      {(report.filters || []).length > 0 && (
        <Card style={{ marginBottom: 14, padding: "10px 14px", overflow: "visible" }}>
          {/* Search + counts */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: (dropdowns.length + numerics.length + dateRanges.length) > 0 ? 10 : 0 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={14} color={T.textMuted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all columns..."
                style={{ width: "100%", padding: "8px 12px 8px 32px", borderRadius: 6, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
            </div>
            <span className="mono" style={{ fontSize: 11, color: T.textDim }}>{filteredData.length} / {processedData.length}</span>
            {hasActiveFilters && <button onClick={() => { setSearch(""); setUserFilters({}); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid " + T.red + "30", background: T.redBg, color: T.red, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>✕ Clear</button>}
          </div>
          {/* Dropdowns — pills for ≤6 values, popup dropdown for >6 */}
          {dropdowns.length > 0 && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: numerics.length > 0 ? 8 : 0 }}>
              {dropdowns.map(f => {
                const vals = dropdownVals[f.fieldId] || [];
                const sel = userFilters[f.fieldId] || [];
                const usePills = vals.length <= 6;
                const getColor = (v) => {
                  if (f.fieldId === "ClientStatus") return STATUS_COLORS[v] || T.textMuted;
                  if (f.fieldId === "BehavioralRisk" || f.fieldId === "EquityRisk") return RISK_COLORS[v] || T.green;
                  if (f.fieldId === "_clientGroup") { const gd = getGroupConfig().groups[v]; return gd ? gd.color : T.textMuted; }
                  if (f.fieldId === "_company") return v === "salam" ? T.amber : T.blue;
                  return T.cyan;
                };
                if (usePills) return (
                  <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginRight: 2 }}>{f.label}:</span>
                    {vals.map(v => {
                      const isAct = sel.includes(v); const pc = getColor(v);
                      return <button key={v} onClick={() => setUserFilters({ ...userFilters, [f.fieldId]: isAct ? sel.filter(x => x !== v) : [...sel, v] })}
                        style={{ padding: "4px 10px", borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: "pointer", border: "1px solid " + (isAct ? pc : T.border), background: isAct ? pc + "15" : "transparent", color: isAct ? pc : T.textDim }}>{v}</button>;
                    })}
                  </div>
                );
                return <FilterSelect key={f.fieldId} label={f.label} values={vals} selected={sel} onChange={newSel => setUserFilters({ ...userFilters, [f.fieldId]: newSel })} />;
              })}
            </div>
          )}
          {/* Numeric + Date + Boolean + Text filters */}
          {(numerics.length + dateRanges.length + booleans.length + textFilters.length) > 0 && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              {numerics.map(f => {
                if (f.type === "above") return (
                  <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{f.label}:</span>
                    <div style={{ display: "flex", alignItems: "center", background: T.bgInput, borderRadius: 5, border: "1px solid " + T.border, overflow: "hidden" }}>
                      <span style={{ padding: "5px 6px", fontSize: 10, color: T.green, fontWeight: 700, background: T.green + "10", borderRight: "1px solid " + T.border }}>≥</span>
                      <input value={userFilters[f.fieldId] ?? f.defaultValue ?? ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: e.target.value })} placeholder="0"
                        style={{ width: 55, padding: "5px 6px", border: "none", background: "transparent", color: T.text, fontSize: 11, outline: "none", fontFamily: "monospace" }} />
                    </div>
                  </div>
                );
                if (f.type === "below") return (
                  <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{f.label}:</span>
                    <div style={{ display: "flex", alignItems: "center", background: T.bgInput, borderRadius: 5, border: "1px solid " + T.border, overflow: "hidden" }}>
                      <span style={{ padding: "5px 6px", fontSize: 10, color: T.red, fontWeight: 700, background: T.red + "10", borderRight: "1px solid " + T.border }}>≤</span>
                      <input value={userFilters[f.fieldId] ?? ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: e.target.value })} placeholder="0"
                        style={{ width: 55, padding: "5px 6px", border: "none", background: "transparent", color: T.text, fontSize: 11, outline: "none", fontFamily: "monospace" }} />
                    </div>
                  </div>
                );
                return (
                  <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{f.label}:</span>
                    <div style={{ display: "flex", alignItems: "center", background: T.bgInput, borderRadius: 5, border: "1px solid " + T.border, overflow: "hidden" }}>
                      <span style={{ padding: "5px 5px", fontSize: 9, color: T.amber, fontWeight: 700, background: T.amber + "10", borderRight: "1px solid " + T.border }}>Min</span>
                      <input value={(userFilters[f.fieldId] || {}).min ?? ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: { ...(userFilters[f.fieldId] || {}), min: e.target.value } })} placeholder="—"
                        style={{ width: 55, padding: "5px 5px", border: "none", background: "transparent", color: T.text, fontSize: 10, outline: "none", fontFamily: "monospace" }} />
                      <span style={{ padding: "5px 5px", fontSize: 9, color: T.amber, fontWeight: 700, background: T.amber + "10", borderLeft: "1px solid " + T.border, borderRight: "1px solid " + T.border }}>Max</span>
                      <input value={(userFilters[f.fieldId] || {}).max ?? ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: { ...(userFilters[f.fieldId] || {}), max: e.target.value } })} placeholder="—"
                        style={{ width: 55, padding: "5px 5px", border: "none", background: "transparent", color: T.text, fontSize: 10, outline: "none", fontFamily: "monospace" }} />
                    </div>
                  </div>
                );
              })}
              {dateRanges.map(f => (
                <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{f.label}:</span>
                  <div style={{ display: "flex", alignItems: "center", background: T.bgInput, borderRadius: 5, border: "1px solid " + T.purple + "30", overflow: "hidden" }}>
                    <input type="date" value={(userFilters[f.fieldId] || {}).from || ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: { ...(userFilters[f.fieldId] || {}), from: e.target.value } })} style={{ padding: "4px 6px", border: "none", background: "transparent", color: T.text, fontSize: 10, outline: "none", colorScheme: "dark" }} />
                    <span style={{ color: T.textMuted, fontSize: 9 }}>→</span>
                    <input type="date" value={(userFilters[f.fieldId] || {}).to || ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: { ...(userFilters[f.fieldId] || {}), to: e.target.value } })} style={{ padding: "4px 6px", border: "none", background: "transparent", color: T.text, fontSize: 10, outline: "none", colorScheme: "dark" }} />
                  </div>
                </div>
              ))}
              {booleans.map(f => (
                <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{f.label}:</span>
                  {["Yes", "No"].map(v => (
                    <button key={v} onClick={() => setUserFilters({ ...userFilters, [f.fieldId]: userFilters[f.fieldId] === v ? "" : v })}
                      style={{ padding: "4px 10px", borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: "pointer", border: "1px solid " + (userFilters[f.fieldId] === v ? (v === "Yes" ? T.red : T.green) : T.border), background: userFilters[f.fieldId] === v ? (v === "Yes" ? T.red : T.green) + "15" : "transparent", color: userFilters[f.fieldId] === v ? (v === "Yes" ? T.red : T.green) : T.textDim }}>{v}</button>
                  ))}
                </div>
              ))}
              {textFilters.map(f => (
                <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase" }}>{f.label}:</span>
                  <input value={userFilters[f.fieldId] || ""} onChange={e => setUserFilters({ ...userFilters, [f.fieldId]: e.target.value })} placeholder="contains..."
                    style={{ width: 100, padding: "5px 8px", borderRadius: 5, border: "1px solid " + T.border, background: T.bgInput, color: T.text, fontSize: 10, outline: "none" }} />
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Table */}
      <DataTable exportTitle={report.name} columns={tableColumns} data={filteredData} pageSize={25} compact
        onRowClick={onClientClick ? (row) => {
          // Find matching client in healthScores
          const match = (C.healthScores || []).find(c => String(c.Login) === String(row.Login) && c._company === row._company);
          if (match) onClientClick(match);
        } : undefined} />
    </div>
  );
}

// ════════════════════════════════════════════════════════
// ═══ MAIN REPORTS PAGE (user-facing) ════════════════
// ════════════════════════════════════════════════════════
// Persist selected report across page navigations (module-level)
let _lastSelectedReport = null;

export default function Reports({ C, onClientClick }) {
  const [configs, setConfigs] = useState(getReportConfigs);
  const [selectedReport, setSelectedReport] = useState(_lastSelectedReport);

  // Keep module var in sync
  useEffect(() => { _lastSelectedReport = selectedReport; }, [selectedReport]);

  useEffect(() => { setConfigs(getReportConfigs()); }, []);

  if (selectedReport) {
    return <ReportViewer report={selectedReport} C={C} onBack={() => setSelectedReport(null)} onClientClick={onClientClick} />;
  }

  if (configs.length === 0) {
    return (
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Reports</h1>
        <p style={{ fontSize: 13, color: T.textDim, marginBottom: 30 }}>Custom reports created by admin will appear here</p>
        <Card style={{ padding: 60, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: T.textDim }}>No Reports Yet</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>Admin can create reports from Admin Panel → Reports tab</div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Reports</h1>
      <p style={{ fontSize: 13, color: T.textDim, marginBottom: 20 }}>Select a report to view, filter and export</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
        {configs.map(r => {
          const accent = r.level === "cu" ? T.amber : T.cyan;
          return (
            <div key={r.id} onClick={() => setSelectedReport(r)} style={{ cursor: "pointer" }}>
            <Card accent={accent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ fontSize: 17, fontWeight: 700 }}>{r.name}</div>
                <span style={{ padding: "3px 10px", borderRadius: 5, fontSize: 9, fontWeight: 700, color: accent, background: accent + "15" }}>{r.level === "cu" ? "CU Level" : "Login Level"}</span>
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 12 }}>{r.description || "No description"}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
                {(r.columns || []).slice(0, 8).map(c => (
                  <span key={c.fieldId} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, color: T.textDim, background: T.bgHover, border: "1px solid " + T.border }}>{c.label}</span>
                ))}
                {(r.columns || []).length > 8 && <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, color: T.textMuted }}>+{r.columns.length - 8}</span>}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid " + T.border }}>
                <span style={{ fontSize: 9, color: T.textMuted }}>{(r.columns || []).length} columns · {(r.filters || []).length} filters</span>
                <span style={{ fontSize: 11, color: accent, fontWeight: 600 }}>Open →</span>
              </div>
            </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Export catalog for admin
export { FIELD_CATALOG, ALL_FIELDS, FIELD_MAP, FILTER_TYPES, FMT_TO_FILTER };
